import React, { useState, useEffect, useRef } from 'react';
import { HiOutlineDotsHorizontal, HiRefresh, HiOutlineTrash } from 'react-icons/hi';
import { Tooltip } from 'flowbite-react';
import { useDashboardService } from '../../../services/dashboard';
import { useSelector } from 'react-redux';

interface ProjectOptionsProps {
  projectId: string;
  projectName?: string;
  onRefresh?: () => void;
  onViewDetails?: () => void;
  onDelete?: () => void;
  lastUpdated?: string;
  updatedBy?: string;
}

/**
 * ProjectOptions component that displays a dropdown menu for project actions
 */
const ProjectOptions: React.FC<ProjectOptionsProps> = ({
  projectId,
  projectName,
  onRefresh,
  onViewDetails,
  onDelete,
  lastUpdated,
  updatedBy
}) => {
  const { checkRefreshStatus, refreshRoadmapGroup } = useDashboardService();
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isRefreshDisabled, setIsRefreshDisabled] = useState(false);
  const [tooltipText, setTooltipText] = useState("");
  const dropdownRef = useRef<HTMLDivElement>(null);
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0 });
  
  const refreshStatus = useSelector((state: any) => 
    state.dashboard.refreshStatus[projectId] || null
  );
  const refreshStatusLoading = useSelector((state: any) => 
    state.dashboard.refreshStatusLoading
  );

  // Format date for display
  const formatDate = (dateString?: string) => {
    if (!dateString) return '';
    try {
      const date = new Date(dateString);
      return new Intl.DateTimeFormat('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
        hour: 'numeric',
        minute: 'numeric',
        hour12: true
      }).format(date);
    } catch (e) {
      return dateString;
    }
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsDropdownOpen(false);
      }
    }
    
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  // Handle dots button click - toggle dropdown and check status
  const handleDotsClick = (e: React.MouseEvent) => {
    // Check status only when opening the dropdown
    if (!isDropdownOpen && projectId) {
      checkRefreshStatus(projectId).catch(error => {
        console.error("Error checking refresh status:", error);
      });
    }
    
    // Calculate dropdown position
    if (dropdownRef.current && e.currentTarget) {
      const buttonRect = e.currentTarget.getBoundingClientRect();
      // Position the dropdown relative to the viewport
      setDropdownPosition({
        top: buttonRect.bottom,
        left: buttonRect.right - 250, // Align right with 250px width
      });
    }
    
    setIsDropdownOpen(!isDropdownOpen);
  };

  // Update UI based on refresh status
  useEffect(() => {
    if (refreshStatus) {
      const status = refreshStatus.status;
      if (status === "cache_in_progress") {
        setIsRefreshDisabled(true);
        setTooltipText("Refresh in progress...");
      } else {
        setIsRefreshDisabled(false);
        setTooltipText(""); // No tooltip when not refreshing
      }
    }
  }, [refreshStatus]);

  // Handle option click
  const handleOptionClick = (type: string) => {
    if (type === 'refresh' && isRefreshDisabled) {
      return; // Do nothing if refresh is disabled
    }
    
    switch (type) {
      case 'refresh':
        // Directly call the refresh API if we have a project ID
        if (projectId) {
          // Only call the refresh API, not the status API
          refreshRoadmapGroup(projectId)
            .then(() => {
              // Call the parent onRefresh if provided
              onRefresh?.();
            })
            .catch(error => {
              console.error("Error refreshing roadmap group:", error);
            });
        } else {
          // Fall back to onRefresh if no projectId
          onRefresh?.();
        }
        break;
      case 'details':
        onViewDetails?.();
        break;
      case 'delete':
        onDelete?.();
        break;
      default:
        break;
    }
    
    // Close the dropdown after handling the action
    setIsDropdownOpen(false);
  };

  // Get status date from refresh status or props
  const getLastUpdatedDate = () => {
    if (refreshStatus?.completed_at) {
      return formatDate(refreshStatus.completed_at);
    }
    return lastUpdated ? formatDate(lastUpdated) : '';
  };

  return (
    <div ref={dropdownRef} className="relative">
      {/* Dots button that triggers dropdown */}
      <button 
        onClick={handleDotsClick}
        className="flex items-center p-1 rounded-md hover:bg-gray-200 dark:hover:bg-gray-700"
      >
        <HiOutlineDotsHorizontal className="h-3 w-3 text-slate-600 dark:text-slate-300" />
      </button>
      
      {/* Portal for dropdown menu to avoid scroll container issues */}
      {isDropdownOpen && (
        <div 
          className="fixed w-56 rounded-md shadow-lg bg-white dark:bg-gray-700 ring-1 ring-black ring-opacity-5 z-50"
          style={{
            top: `${dropdownPosition.top}px`,
            left: `${dropdownPosition.left}px`,
          }}
        >
          <div className="py-1" role="menu" aria-orientation="vertical">
            {/* Refresh Option */}
            {isRefreshDisabled ? (
              <Tooltip content={tooltipText} placement="left" trigger="hover">
                <button
                  className="w-full text-left px-4 py-2 text-sm flex flex-col opacity-50 cursor-not-allowed"
                  disabled
                >
                  <div className="flex items-center gap-2">
                    <HiRefresh className="h-4 w-4 animate-spin text-purple-500" /> 
                    <span className="text-purple-600 dark:text-purple-400">Please wait</span>
                  </div>
                  {getLastUpdatedDate() && (
                    <span className="text-xs text-slate-500 dark:text-slate-400 mt-1 ml-6">
                      Last updated: {getLastUpdatedDate()}
                    </span>
                  )}
                </button>
              </Tooltip>
            ) : (
              <button
                className="w-full text-left px-4 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-600 hover:text-gray-900 dark:hover:text-white"
                role="menuitem"
                onClick={() => handleOptionClick('refresh')}
              >
                <div className="flex flex-col">
                  <div className="flex items-center gap-2">
                    <HiRefresh className="h-4 w-4" /> 
                    <span>Refresh</span>
                  </div>
                  {getLastUpdatedDate() && (
                    <span className="text-xs text-slate-500 dark:text-slate-400 mt-1 ml-6">
                      Last updated: {getLastUpdatedDate()}
                    </span>
                  )}
                </div>
              </button>
            )}
            
            {/* View Details Option */}
            {onViewDetails && (
              <button
                className="w-full text-left px-4 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-600 hover:text-gray-900 dark:hover:text-white"
                role="menuitem"
                onClick={() => handleOptionClick('details')}
              >
                <div className="flex items-center gap-2">
                  <HiOutlineDotsHorizontal className="h-4 w-4" /> View Details
                </div>
              </button>
            )}
            
            {/* Delete Option */}
            {onDelete && (
              <button
                className="w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-red-50 dark:text-red-400 dark:hover:bg-red-900/30"
                role="menuitem"
                onClick={() => handleOptionClick('delete')}
              >
                <div className="flex items-center gap-2">
                  <HiOutlineTrash className="h-4 w-4" /> Delete Project
                </div>
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default ProjectOptions; 